import type {
	TemplateVariableDefinition,
	TemplateVariableType,
	VariableTypeKey,
} from '@supply-mover-2-0/shared';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/forms/Button';
import { TemplateVariableEditor } from '../../components/forms/TemplateVariableEditor';
import { Sidebar } from '../../components/overlays/Sidebar';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';

/**
 * Custom field definition for automation start step.
 * Uses the same structure as TemplateVariableDefinition for consistency.
 */
export interface CustomField {
	name: string;
	type: TemplateVariableType;
	required: boolean;
	defaultValue?: unknown;
	description?: string;
	arrayType?: VariableTypeKey;
	objectType?: VariableTypeKey;
}

export interface StartNodeConfig {
	inputType: 'entity' | 'custom';
	entityType?: 'client' | 'order' | 'quote' | 'invoice' | 'vendor' | 'item';
	customFields?: Array<CustomField>;
}

interface StartNodeConfigSidebarProps {
	visible: boolean;
	onCancel: () => void;
	onSave: (config: StartNodeConfig) => void;
	initialConfig?: StartNodeConfig;
	/** When true, render inline (no Sidebar overlay) — used by the 2.0 builder. */
	inline?: boolean;
	/**
	 * Inline (always-save) mode: called on every edit so the host commits the
	 * change live. The host keeps the pre-edit config as the Revert snapshot.
	 */
	onChange?: (config: StartNodeConfig) => void;
}

/**
 * Map legacy field types to template variable types.
 * Legacy types: 'text' | 'number' | 'phone' | 'email' | 'date'
 * New types: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object'
 */
function mapLegacyType(type: string): TemplateVariableType {
	switch (type) {
		case 'text':
		case 'phone':
		case 'email':
			return 'string';
		case 'number':
			return 'number';
		case 'date':
			return 'date';
		default:
			return type as TemplateVariableType;
	}
}

/**
 * Convert CustomField to TemplateVariableDefinition for the editor.
 */
function customFieldToVariable(field: CustomField): TemplateVariableDefinition {
	return {
		name: field.name,
		type: mapLegacyType(field.type),
		required: field.required,
		defaultValue: field.defaultValue,
		description: field.description,
		arrayType: field.arrayType,
		objectType: field.objectType,
	};
}

/**
 * Convert TemplateVariableDefinition back to CustomField for saving.
 */
function variableToCustomField(
	variable: TemplateVariableDefinition,
): CustomField {
	return {
		name: variable.name,
		type: variable.type,
		required: variable.required,
		defaultValue: variable.defaultValue,
		description: variable.description,
		arrayType: variable.arrayType,
		objectType: variable.objectType,
	};
}

const StartNodeConfigSidebar: React.FC<StartNodeConfigSidebarProps> = ({
	visible,
	onCancel,
	onSave,
	initialConfig,
	inline,
	onChange,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const [variables, setVariables] = useState<Array<TemplateVariableDefinition>>(
		() => {
			if (initialConfig?.customFields != null) {
				return initialConfig.customFields.map(customFieldToVariable);
			}

			return [];
		},
	);
	// When true, the live-commit watcher skips the next `variables` change
	// because it was produced by (re-)initialization, not a user edit.
	const skipCommitRef = useRef(true);

	useEffect(() => {
		setVariables(
			initialConfig?.customFields != null
				? initialConfig.customFields.map(customFieldToVariable)
				: [],
		);
		// The setVariables above is initialization — not a user edit.
		skipCommitRef.current = true;
	}, [initialConfig]);

	// Inline (always-save) mode: every edit is pushed to the host immediately
	// so switching steps never loses work. The first change after each
	// (re-)initialization is the initialization itself and is skipped.
	useEffect(() => {
		if (inline !== true || onChange == null) {
			return;
		}
		if (skipCommitRef.current) {
			skipCommitRef.current = false;
			return;
		}
		const validVariables = variables.filter(
			(v) => v.name != null && v.name.trim() !== '',
		);
		onChange({
			inputType: 'custom',
			customFields: validVariables.map(variableToCustomField),
		});
	}, [variables, inline, onChange]);

	const handleSave = () => {
		const validVariables = variables.filter(
			(v) => v.name != null && v.name.trim() !== '',
		);

		const config: StartNodeConfig = {
			inputType: 'custom',
			customFields: validVariables.map(variableToCustomField),
		};

		onSave(config);
	};

	const isValid = () =>
		variables.length === 0 ||
		variables.every((v) => v.name != null && v.name.trim() !== '');

	// Pre-edit snapshot for the inline Revert action.
	const originalVariables = useMemo<Array<TemplateVariableDefinition>>(
		() =>
			initialConfig?.customFields != null
				? initialConfig.customFields.map(customFieldToVariable)
				: [],
		[initialConfig],
	);
	const isDirty =
		JSON.stringify(variables) !== JSON.stringify(originalVariables);

	/** Discard inline edits, restoring the start step's pre-edit variables. */
	const handleRevert = () => {
		setVariables(originalVariables);
	};

	const body = (
		<div className="space-y-4 p-6">
			<p className={`text-sm ${colors.classes.textSecondary}`}>
				{t('Define what data this flow requires to run')}
			</p>

			<p className={`text-xs ${colors.classes.textSecondary}`}>
				{t(
					'Define the variables that will be available as Start.* in your flow',
				)}
			</p>

			<TemplateVariableEditor
				variables={variables}
				onChange={setVariables}
				showCard={false}
				title={t('Input Variables')}
				emptyMessage={t('No input variables defined. Click Add to create one.')}
			/>
		</div>
	);

	// Inline mode (2.0 builder): the config expands under the start node.
	if (inline === true) {
		return (
			<div
				className={`rounded-lg border ${colors.classes.borderDefault} ${colors.classes.bgPrimary}`}
			>
				{body}
				<div className="flex items-center justify-between px-6 pb-6">
					<span className={`text-xs ${colors.classes.textSecondary}`}>
						{t('Changes are saved automatically')}
					</span>
					<div className="flex gap-2">
						<Button
							variant="secondary"
							onClick={handleRevert}
							disabled={!isDirty}
						>
							{t('Revert changes')}
						</Button>
						<Button variant="primary" onClick={onCancel}>
							{t('Done')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<Sidebar
			isOpen={visible}
			onClose={onCancel}
			title={t('Configure Start Step')}
			width="lg"
			position="right"
			actions={[
				{
					label: t('Cancel'),
					onClick: onCancel,
					variant: 'secondary',
				},
				{
					label: t('Save'),
					onClick: handleSave,
					variant: 'primary',
					disabled: !isValid(),
				},
			]}
		>
			{body}
		</Sidebar>
	);
};

export default StartNodeConfigSidebar;
